import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useViewerModeStore, ViewerMode } from '@/modules/viewer/store/useViewerModeStore';
import { ItemSharingMode, useViewerItemStore } from '@/modules/viewer/store/useViewerItemStore';
import { useUser } from '@/modules/viewer/composables/useUser';
import { useUserStore } from '@/store/useUserStore';
import { DEBUG_NAMESPACE, useDebug } from '@/composables/useDebug';
import { useToast } from '@/composables/useToast';

/**
 * Switches the active viewer mode, persisting the choice to the item's
 * `viewer_layout` when the caller has edit access. Guests and view-only
 * teammates change the mode for their session only.
 */
export const useCanPersistViewerLayout = () => {
  const viewerItemStore = useViewerItemStore();
  const userStore = useUserStore();
  const { isCurrentItemOwner, isSameOrgAsItem } = useUser();
  const { isLoggedIn, isOrgUserViewOnly } = storeToRefs(userStore);
  const { sharing, sharingSpecificUsers } = storeToRefs(viewerItemStore);

  return computed(() => {
    if (isCurrentItemOwner.value) {
      return true;
    }
    if (!isLoggedIn.value) {
      return false;
    }
    if (sharing.value === ItemSharingMode.SPECIFIC_USER_EDIT) {
      return sharingSpecificUsers.value?.includes(userStore.email ?? '') ?? false;
    }
    // Keep this explicit set aligned with the edit-capable sharing modes in
    // Pundit: any new mode must be intentionally added here before clients
    // can persist a viewer-layout preference through this path.
    return isSameOrgAsItem.value && !isOrgUserViewOnly.value && sharing.value === ItemSharingMode.ORGANIZATION_EDIT;
  });
};

export const useViewerModeSwitch = () => {
  const viewerModeStore = useViewerModeStore();
  const { currentMode } = storeToRefs(viewerModeStore);
  const viewerItemStore = useViewerItemStore();
  const canPersistViewerLayout = useCanPersistViewerLayout();
  const { debug } = useDebug(DEBUG_NAMESPACE.VIEWER_ITEM);
  const { error } = useToast();

  const switchMode = (mode: ViewerMode) => {
    if (mode === currentMode.value) {
      return;
    }
    const previousMode = currentMode.value;
    viewerModeStore.setMode(mode);
    if (canPersistViewerLayout.value) {
      viewerItemStore.updateViewerLayout(mode).catch(e => {
        if (currentMode.value === mode) {
          viewerModeStore.setMode(previousMode);
        }
        debug('Cannot persist viewer layout', e);
        error('Could not save viewer mode. Please try again.');
      });
    }
  };

  return { switchMode };
};
